import { useState, useEffect } from "react";
import axios from "axios";
import { Lock, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { API, setToken, formatApiError } from "../../lib/admin";

export const AdminAuth = ({ onAuthenticated }) => {
  const [mode, setMode] = useState("loading"); // 'loading' | 'setup' | 'login'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/status`)
      .then(({ data }) => {
        setEmail(data.admin_email || "");
        setMode(data.password_set ? "login" : "setup");
      })
      .catch(() => setMode("login"));
  }, []);

  const handleSetup = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/setup-password`, { password });
      setToken(data.token);
      onAuthenticated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Setup failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setToken(data.token);
      onAuthenticated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6" data-testid="admin-auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-water/15 border border-water/25 flex items-center justify-center">
            <ShieldCheck size={26} className="text-water" />
          </div>
          <div className="label-caps text-[11px] text-water/70 mt-5 tracking-[0.2em]">Bay Quarter Shores</div>
          <h1 className="font-serif text-3xl font-light text-white mt-2">Lead Management</h1>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]">
          {mode === "loading" && (
            <div className="flex items-center justify-center py-10 text-warm">
              <Loader2 className="animate-spin" size={22} />
            </div>
          )}

          {mode === "setup" && (
            <form onSubmit={handleSetup} data-testid="admin-setup-form">
              <div className="flex items-center gap-2 text-navy mb-1">
                <KeyRound size={18} className="text-water" />
                <h2 className="font-serif text-2xl font-light">Set Your Password</h2>
              </div>
              <p className="text-sm text-warm mb-6">
                Welcome. Create a password for <span className="font-medium text-navy">{email}</span> to secure your dashboard.
              </p>
              <div className="space-y-4">
                <div>
                  <Label className="text-navy">New Password</Label>
                  <Input
                    type="password"
                    data-testid="admin-setup-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="mt-2 h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-navy">Confirm Password</Label>
                  <Input
                    type="password"
                    data-testid="admin-setup-confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter password"
                    className="mt-2 h-12 rounded-xl"
                  />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="admin-auth-error">{error}</p>}
                <button
                  type="submit"
                  data-testid="admin-setup-submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-water text-white rounded-full px-8 py-3.5 font-medium hover:bg-water-soft transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Lock size={17} />}
                  Set Password &amp; Continue
                </button>
              </div>
            </form>
          )}

          {mode === "login" && (
            <form onSubmit={handleLogin} data-testid="admin-login-form">
              <div className="flex items-center gap-2 text-navy mb-1">
                <Lock size={18} className="text-water" />
                <h2 className="font-serif text-2xl font-light">Sign In</h2>
              </div>
              <p className="text-sm text-warm mb-6">Access the lead management dashboard.</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-navy">Email</Label>
                  <Input
                    type="email"
                    data-testid="admin-login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-navy">Password</Label>
                  <Input
                    type="password"
                    data-testid="admin-login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className="mt-2 h-12 rounded-xl"
                  />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="admin-auth-error">{error}</p>}
                <button
                  type="submit"
                  data-testid="admin-login-submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-water text-white rounded-full px-8 py-3.5 font-medium hover:bg-water-soft transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Lock size={17} />}
                  Sign In
                </button>
              </div>
            </form>
          )}
        </div>
        <p className="text-center text-white/40 text-xs mt-6">Authorized access only.</p>
      </div>
    </div>
  );
};
